import { useState } from 'react';

import {
  SECTION_PROPERTY,
  useAuditConfigQuery,
  usePreviewAuditConfigMutation,
  useSaveAuditConfigMutation,
  type AllAuditConfig,
  type AuditConfigSection,
  type AuditConfigSectionKey,
} from '../../api/audit-config-queries';
import { describeApiError } from '../../api/apiClient';
import { useWritePermission } from '../../api/permission-queries';

/** Stable JSON for equality checks: object keys sorted, array order preserved. */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_key, val: unknown) =>
    typeof val === 'object' && val !== null && !Array.isArray(val)
      ? Object.fromEntries(
          Object.keys(val)
            .sort()
            .map((k) => [k, (val as Record<string, unknown>)[k]])
        )
      : val
  );
}

interface ConfigSectionEditorOptions<Row, Value> {
  section: AuditConfigSectionKey;
  /** Human label used in messages ("Expected Gaps saved"). */
  label: string;
  /** Server value → editable rows. Must satisfy fromRows(toRows(v)) ≡ v. */
  toRows: (value: Value) => Row[];
  /** Editable rows → value sent to the server. */
  fromRows: (rows: Row[]) => Value;
  /** Client-side checks that would otherwise silently lose data; null when valid. */
  validateRows?: (rows: Row[]) => string | null;
}

/**
 * Shared editing state for an audit-config Settings section.
 *
 * - Rows are derived from the server's stored value and re-synced whenever that value
 *   changes, unless the user has local edits (then a conflict is flagged instead).
 * - `isDirty` compares the rows against the current server value.
 * - `apply()` PUTs the value and keeps the preview dialog open until the server
 *   confirms. The grid always shows what the server stored; if that differs from what
 *   was sent, an error is shown rather than silently keeping local state.
 */
export function useConfigSectionEditor<Row, Value>({
  section,
  label,
  toRows,
  fromRows,
  validateRows,
}: ConfigSectionEditorOptions<Row, Value>) {
  const query = useAuditConfigQuery();
  const saveMutation = useSaveAuditConfigMutation();
  const previewMutation = usePreviewAuditConfigMutation();
  const permission = useWritePermission();

  const record = query.data?.[SECTION_PROPERTY[section]] as AuditConfigSection<Value> | undefined;
  const serverKey = record ? canonicalJson(record.value) : null;

  const [rows, setRows] = useState<Row[]>([]);
  const [syncedKey, setSyncedKey] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const localKey = canonicalJson(fromRows(rows));
  const hasLocalEdits = syncedKey !== null && localKey !== syncedKey;

  // Adjust state during render when the server value changes (React's recommended
  // alternative to a sync effect). Local edits are never overwritten; if the server
  // now holds exactly the local edits (our save landed), adopt it as the baseline.
  if (record && serverKey !== syncedKey && (!hasLocalEdits || serverKey === localKey)) {
    setRows(toRows(record.value));
    setSyncedKey(serverKey);
  }

  const isDirty = record !== undefined && localKey !== serverKey;
  const hasConflict = hasLocalEdits && serverKey !== syncedKey;
  const validationError = validateRows ? validateRows(rows) : null;

  /**
   * @param committedRows - rows with any in-progress grid edits applied (see
   *   usePendingGridEdits); they become the editor state and the proposed value.
   */
  function openPreview(committedRows?: Row[]): void {
    const proposedRows = committedRows ?? rows;
    if (validateRows?.(proposedRows)) {
      if (committedRows) setRows(committedRows);
      return;
    }
    if (committedRows) setRows(committedRows);
    setApplyError(null);
    previewMutation.reset();
    previewMutation.mutate({ section, proposedValue: fromRows(proposedRows) });
    setPreviewOpen(true);
  }

  function closePreview(): void {
    if (saveMutation.isPending) return;
    setPreviewOpen(false);
    setApplyError(null);
    previewMutation.reset();
  }

  async function apply(): Promise<void> {
    if (!permission.canWrite) {
      setApplyError(permission.reason);
      return;
    }
    const sent = fromRows(rows);
    setApplyError(null);
    try {
      const saved = await saveMutation.mutateAsync({ section, value: sent });
      const savedValue = saved.value as Value;
      const savedKey = canonicalJson(savedValue);
      setRows(toRows(savedValue));
      setSyncedKey(savedKey);
      if (savedKey !== canonicalJson(sent)) {
        setApplyError(
          'The server stored a different value than was sent. The editor now shows the ' +
            'stored value — review it before saving again.'
        );
        return;
      }
      setPreviewOpen(false);
      previewMutation.reset();
      setSuccessMessage(`${label} saved`);
    } catch (error: unknown) {
      setApplyError(`Save failed — ${describeApiError(error)}`);
    }
  }

  /** Discard local edits and show the server value again. */
  function discardChanges(): void {
    if (!record) return;
    setRows(toRows(record.value));
    setSyncedKey(serverKey);
  }

  return {
    data: query.data as AllAuditConfig | undefined,
    record,
    isLoading: query.isLoading,
    loadError: query.error ? describeApiError(query.error) : null,
    rows,
    setRows,
    isDirty,
    hasConflict,
    validationError,
    discardChanges,
    preview: {
      open: previewOpen,
      data: previewMutation.data ?? null,
      isPending: previewMutation.isPending,
      error: previewMutation.error ? describeApiError(previewMutation.error) : null,
    },
    openPreview,
    closePreview,
    apply,
    applying: saveMutation.isPending,
    /** Tooltip reason when the user may not apply changes (null when allowed). */
    writeDisabledReason: permission.reason,
    applyError,
    successMessage,
    clearSuccess: () => setSuccessMessage(null),
  };
}
